import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2 } from "lucide-react";
import Seo, { localBusinessJsonLd, breadcrumbJsonLd } from "@/components/Seo";
import { SITE } from "@/mock";

export default function Contact() {
  const [params] = useSearchParams();
  const productParam = params.get("product") || "";
  const sizeParam = params.get("size") || "";
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: productParam
      ? `I'm interested in the ${productParam}${sizeParam ? ` (${sizeParam})` : ""}. Please send me pricing and availability.`
      : "",
  });
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Contact", path: "/contact" },
  ];

  const submit = (e) => {
    e.preventDefault();
    // MOCK: frontend-only submission (stored to state). Wire to backend later.
    try {
      const list = JSON.parse(localStorage.getItem("mt_contacts") || "[]");
      list.push({ ...form, at: new Date().toISOString() });
      localStorage.setItem("mt_contacts", JSON.stringify(list));
    } catch (_) {}
    setSent(true);
  };

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  return (
    <>
      <Seo
        title="Contact MattressTown in Fort Lauderdale"
        description="Contact MattressTown in Fort Lauderdale, FL. Call, email or visit our showroom at 2677 N Federal Hwy for expert help finding your perfect mattress."
        path="/contact"
        jsonLd={[localBusinessJsonLd(), breadcrumbJsonLd(crumbs)]}
      />

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold uppercase text-neutral-900 sm:text-4xl">Contact Us</h1>
        <p className="mt-3 max-w-2xl text-neutral-600">Have a question or need help choosing a mattress? Reach out - our family is here to help.</p>

        <div className="mt-8 grid gap-8 sm:mt-10 sm:gap-10 lg:grid-cols-2">
          <div className="space-y-4">
            <a href={SITE.phoneHref} className="flex items-center gap-4 rounded-xl border border-neutral-200 bg-white p-5 hover-lift">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#D0021B]/10"><Phone className="h-5 w-5 text-[#D0021B]" aria-hidden="true" /></span>
              <span><span className="block text-xs font-semibold uppercase tracking-wide text-neutral-500">Call Us</span><span className="font-bold text-neutral-900">{SITE.phone}</span></span>
            </a>
            <a href={`mailto:${SITE.email}`} className="flex items-center gap-4 rounded-xl border border-neutral-200 bg-white p-5 hover-lift">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#D0021B]/10"><Mail className="h-5 w-5 text-[#D0021B]" aria-hidden="true" /></span>
              <span><span className="block text-xs font-semibold uppercase tracking-wide text-neutral-500">Email</span><span className="font-bold text-neutral-900">{SITE.email}</span></span>
            </a>
            <div className="flex items-start gap-4 rounded-xl border border-neutral-200 bg-white p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#D0021B]/10"><MapPin className="h-5 w-5 text-[#D0021B]" aria-hidden="true" /></span>
              <span><span className="block text-xs font-semibold uppercase tracking-wide text-neutral-500">Showroom</span><span className="font-bold text-neutral-900">{SITE.address.full}</span></span>
            </div>
            <div className="flex items-start gap-4 rounded-xl border border-neutral-200 bg-white p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#D0021B]/10"><Clock className="h-5 w-5 text-[#D0021B]" aria-hidden="true" /></span>
              <span><span className="block text-xs font-semibold uppercase tracking-wide text-neutral-500">Hours</span><ul className="text-sm text-neutral-700">{SITE.hours.map((h) => (<li key={h.day}>{h.day}: {h.time}</li>))}</ul></span>
            </div>
          </div>

          <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
            {sent ? (
              <div className="flex flex-col items-center py-16 text-center">
                <CheckCircle2 className="h-14 w-14 text-green-600" aria-hidden="true" />
                <h2 className="mt-4 text-2xl font-bold text-neutral-900">Thank You!</h2>
                <p className="mt-2 text-neutral-600">We received your message and will get back to you soon.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <h2 className="text-xl font-bold uppercase text-neutral-900">Send a Message</h2>
                <div>
                  <label htmlFor="name" className="mb-1 block text-sm font-semibold text-neutral-800">Name</label>
                  <input id="name" name="name" required value={form.name} onChange={onChange} className="w-full rounded-lg border border-neutral-300 px-4 py-2.5 outline-none focus:border-[#D0021B]" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="email" className="mb-1 block text-sm font-semibold text-neutral-800">Email</label>
                    <input id="email" name="email" type="email" required value={form.email} onChange={onChange} className="w-full rounded-lg border border-neutral-300 px-4 py-2.5 outline-none focus:border-[#D0021B]" />
                  </div>
                  <div>
                    <label htmlFor="phone" className="mb-1 block text-sm font-semibold text-neutral-800">Phone</label>
                    <input id="phone" name="phone" value={form.phone} onChange={onChange} className="w-full rounded-lg border border-neutral-300 px-4 py-2.5 outline-none focus:border-[#D0021B]" />
                  </div>
                </div>
                <div>
                  <label htmlFor="message" className="mb-1 block text-sm font-semibold text-neutral-800">Message</label>
                  <textarea id="message" name="message" data-testid="contact-message" rows={5} required value={form.message} onChange={onChange} className="w-full rounded-lg border border-neutral-300 px-4 py-2.5 outline-none focus:border-[#D0021B]" />
                </div>
                <button type="submit" className="inline-flex items-center gap-2 rounded-full bg-[#D0021B] px-8 py-3 text-sm font-bold uppercase tracking-wide text-white hover:bg-[#a80016]">
                  <Send className="h-4 w-4" aria-hidden="true" /> Send Message
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Map */}
        <section className="mt-10 sm:mt-12" aria-label="Map to our showroom">
          <h2 className="text-2xl font-bold uppercase text-neutral-900">Find Our Showroom</h2>
          <p className="mt-2 text-neutral-600">{SITE.address.full}</p>
          <div className="mt-5 overflow-hidden rounded-2xl border border-neutral-200 shadow-sm">
            <iframe
              title="Map of MattressTown, Fort Lauderdale"
              src="https://www.google.com/maps?q=2677+N+Federal+Hwy,+Fort+Lauderdale,+FL+33306&output=embed"
              className="h-[380px] w-full border-0 sm:h-[440px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
          <div className="mt-5">
            <a
              href={SITE.address.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#D0021B] px-7 py-3 text-sm font-bold uppercase tracking-wide text-white hover:bg-[#a80016]"
            >
              <MapPin className="h-4 w-4" aria-hidden="true" /> Get Directions
            </a>
          </div>
        </section>
      </div>
    </>
  );
}
